import React from "react";
import { QRCodeSVG } from "qrcode.react";
import Swal from "sweetalert2";
import { Input } from "../../../components/Input";
import { Button } from "../../../components/Button";
import { Titulo, Texto, InputsContainer, Botoes } from "../box_styles";
import { AreaQr, Passos, Situacao } from "./styles";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import Form from "../../../components/Form";
import {
  DoisFatoresApi,
  DoisFatoresStatus,
  codigoErroDoisFatores,
  mensagemErroDoisFatores,
} from "../../../hooks/doisFatoresApi";

interface DoisFatoresBoxProps {
  setDoisFatoresBox: (newValue: boolean) => void;
  status: DoisFatoresStatus | null;
  onChange: () => void;
}

type Etapa = "senha" | "qr" | "desativar";

export const DoisFatoresBox: React.FC<DoisFatoresBoxProps> = ({
  setDoisFatoresBox,
  status,
  onChange,
}) => {
  const { theme } = useTheme();
  const api = DoisFatoresApi();

  const [etapa, setEtapa] = React.useState<Etapa>(status?.ativo ? "desativar" : "senha");
  const [formValid, setFormValid] = React.useState<boolean>(false);
  const [carregando, setCarregando] = React.useState<boolean>(false);

  const [senha, setSenha] = React.useState<string>("");
  const [codigo, setCodigo] = React.useState<string>("");
  const [uri, setUri] = React.useState<string>("");
  const [segredo, setSegredo] = React.useState<string>("");

  const erro = (error: any) => {
    Swal.fire({
      icon: "error",
      title: "Oops...",
      text: mensagemErroDoisFatores(codigoErroDoisFatores(error)),
    });
  };

  const fechar = () => {
    setDoisFatoresBox(false);
  };

  const handleSenha = async () => {
    setCarregando(true);
    try {
      const dados = await api.iniciar(senha);
      setUri(dados.uri);
      setSegredo(dados.segredo);
      setCodigo("");
      setEtapa("qr");
    } catch (error) {
      erro(error);
    } finally {
      setCarregando(false);
    }
  };

  const handleAtivar = async () => {
    setCarregando(true);
    try {
      await api.confirmar(codigo);
      onChange();
      fechar();
      Swal.fire({
        icon: "success",
        title: "Autenticação em duas etapas ativada!",
        text: "A partir do próximo acesso vamos pedir o código do aplicativo.",
      });
    } catch (error) {
      erro(error);
    } finally {
      setCarregando(false);
    }
  };

  const handleDesativar = async () => {
    setCarregando(true);
    try {
      await api.desativar(senha, codigo);
      onChange();
      fechar();
      Swal.fire({
        icon: "success",
        title: "Autenticação em duas etapas desativada",
        text: "Seu acesso volta a exigir apenas e-mail e senha.",
      });
    } catch (error) {
      erro(error);
    } finally {
      setCarregando(false);
    }
  };

  const copiarChave = () => {
    navigator.clipboard?.writeText(segredo);
    Swal.fire({
      icon: "success",
      title: "Chave copiada!",
      showConfirmButton: false,
      timer: 1200,
    });
  };

  const botaoCancelar = (
    <div className="cancelar" onClick={fechar}>
      Cancelar
    </div>
  );

  if (etapa === "desativar") {
    return (
      <Form
        className="view-DoisFatoresBox"
        id="view-DoisFatoresBoxDesativarForm"
        setFormValid={setFormValid}
        onSubmit={handleDesativar}
      >
        <Titulo theme={theme}>Autenticação de dois fatores</Titulo>
        <Situacao theme={theme}>
          <span className="bolinha"></span>
          <span>
            Ativa
            {status?.ativado_em ? ` desde ${status.ativado_em.substring(0, 10).split("-").reverse().join("/")}` : ""}
          </span>
        </Situacao>
        <Texto theme={theme}>
          Para desativar, confirme sua senha e um código do aplicativo. Depois
          disso o acesso volta a pedir apenas e-mail e senha.
        </Texto>
        <InputsContainer theme={theme}>
          <Input
            label="Senha atual"
            type="password"
            required={true}
            value={senha}
            onChange={setSenha}
            placeholder="Senha atual"
            allowEye={true}
          />
          <Input
            label="Código do aplicativo"
            type="text"
            required={true}
            minLength={6}
            maxLength={6}
            value={codigo}
            onChange={(newValue) => setCodigo(newValue.replace(/\D/g, ""))}
            placeholder="000000"
          />
          <Botoes theme={theme}>
            <Button
              title="Desativar"
              className={`botao_alterar ${formValid ? "" : "disabled"}`}
              width="50%"
              type="submit"
              loading={carregando}
            />
            {botaoCancelar}
          </Botoes>
        </InputsContainer>
      </Form>
    );
  }

  if (etapa === "qr") {
    return (
      <Form
        className="view-DoisFatoresBox"
        id="view-DoisFatoresBoxQrForm"
        setFormValid={setFormValid}
        onSubmit={handleAtivar}
      >
        <Titulo theme={theme}>Autenticação de dois fatores</Titulo>
        <Passos theme={theme}>
          <li>Instale o Google Authenticator no seu celular.</li>
          <li>Toque em "+" e escolha "Ler código QR".</li>
          <li>Aponte para o código abaixo e digite os 6 dígitos que aparecerem.</li>
        </Passos>
        <AreaQr theme={theme}>
          <div className="moldura">
            <QRCodeSVG value={uri} size={180} />
          </div>
          <div className="rotulo-chave">Não consegue ler o código? Digite a chave:</div>
          <div className="chave" onClick={copiarChave} title="Clique para copiar">
            {segredo}
          </div>
        </AreaQr>
        <InputsContainer theme={theme}>
          <Input
            label="Código do aplicativo"
            type="text"
            required={true}
            minLength={6}
            maxLength={6}
            value={codigo}
            onChange={(newValue) => setCodigo(newValue.replace(/\D/g, ""))}
            placeholder="000000"
          />
          <Botoes theme={theme}>
            <Button
              title="Ativar"
              className={`botao_alterar ${formValid ? "" : "disabled"}`}
              width="50%"
              type="submit"
              loading={carregando}
            />
            {botaoCancelar}
          </Botoes>
        </InputsContainer>
      </Form>
    );
  }

  return (
    <Form
      className="view-DoisFatoresBox"
      id="view-DoisFatoresBoxSenhaForm"
      setFormValid={setFormValid}
      onSubmit={handleSenha}
    >
      <Titulo theme={theme}>Autenticação de dois fatores</Titulo>
      <Texto theme={theme}>
        Com a autenticação de dois fatores ativa, além da senha vamos pedir um
        código de 6 dígitos gerado pelo Google Authenticator toda vez que você
        entrar. Confirme sua senha para começar.
      </Texto>
      <InputsContainer theme={theme}>
        <Input
          label="Senha atual"
          type="password"
          required={true}
          value={senha}
          onChange={setSenha}
          placeholder="Senha atual"
          allowEye={true}
        />
        <Botoes theme={theme}>
          <Button
            title="Continuar"
            className={`botao_alterar ${formValid ? "" : "disabled"}`}
            width="50%"
            type="submit"
            loading={carregando}
          />
          {botaoCancelar}
        </Botoes>
      </InputsContainer>
    </Form>
  );
};
